import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Action, Module } from '@mygmao/shared';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { AppLayout } from './layout/AppLayout';
import { AccessDeniedPage } from '../pages/AccessDeniedPage';

/**
 * Garde de route (US-DASH-03 + US-TR-11).
 *
 *  - Non authentifié → redirige `/login`.
 *  - Authentifié sans `module/action` → autorisé, enveloppé dans `<AppLayout>`.
 *  - Authentifié avec `module/action` → vérifie via `usePermissions().can()` :
 *      • OK → rendu enveloppé dans `<AppLayout>`.
 *      • KO → `<AccessDeniedPage>` plein écran (US-TR-11) — la page fournit
 *        elle-même les actions de sortie (retour dashboard, logout) et doit
 *        être centrée sur tout le viewport.
 */
interface ProtectedRouteProps {
  children: ReactNode;
  module?: Module;
  action?: Action;
}

export function ProtectedRoute({ children, module, action }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (module && action && !can(module, action)) {
    return <AccessDeniedPage module={module} action={action} />;
  }

  return <AppLayout>{children}</AppLayout>;
}
